import { DEFAULT_SORT_ORDER } from '@/components/layout/Navigation/sessions/header/toolbar/filters/atoms';
import { getSessionGroupRenderOrder } from '@/features/sessions/hooks/queries/sessionGroupParams';
import type { SessionListItem } from '@/features/sessions/types';
import { withoutPendingServerDeletes } from '@heidi/session/contracts';
import type { CachedSessionsListWindow } from '@heidi/session/data';

import {
  getSessionDateGroupRange,
  getSessionListDateGroup,
  type SessionDateGroup,
} from './date-group';

/**
 * The window the default grouped list fetches for one date group: its exact
 * bounds, plus the sort order it is keyed under. Upcoming is fetched ascending
 * while it is collapsed, so that group is accepted in either order; the rest
 * must be the default order or they belong to a different view's query.
 */
const findGroupWindow = (
  windows: readonly CachedSessionsListWindow[],
  group: SessionDateGroup,
  now: Date
): CachedSessionsListWindow | undefined => {
  const range = getSessionDateGroupRange(group, now);
  return windows.find(
    (window) =>
      window.from === range.from?.toISOString() &&
      window.to === range.to?.toISOString() &&
      (group === 'upcoming' || window.sortOrder === DEFAULT_SORT_ORDER)
  );
};

/**
 * The session the list's own restore (`pickRestoreSessionId`) would open,
 * derived from rows the v2.2 list has ALREADY cached — or undefined when that
 * cannot be established from cache alone, in which case the caller keeps its
 * existing navigation.
 *
 * Supported case only, deliberately: the default list, no Upcoming rows. With
 * Upcoming empty the render order collapses to today → yesterday → older and
 * both states of the collapsed-Upcoming flag agree on the target. An Upcoming
 * row makes the choice depend on that flag and on the Scribe page's layout
 * (neither visible to a caller outside the list), so it bails rather than risk
 * a different session than restore would pick.
 *
 * Two things make a cached snapshot unusable, and each returns undefined: a
 * group window that is absent or has not resolved (missing rows are not empty
 * rows — the list would go on to select from them), and any Upcoming row. A
 * row that classifies into a LATER group than the window it was fetched for is
 * not one of them — it is left to the group it renders in and the walk moves
 * on. Windows belonging to another view — a date-range filter's own bounds, or
 * yesterday's group ranges still in cache after midnight — are not this view's
 * windows and are never read.
 */
export function pickCachedRestoreSessionId({
  windows,
  now,
  pendingServerDeleteIds,
}: {
  windows: readonly CachedSessionsListWindow[];
  now: Date;
  pendingServerDeleteIds: readonly string[];
}): string | undefined {
  for (const group of getSessionGroupRenderOrder(DEFAULT_SORT_ORDER)) {
    const window = findGroupWindow(windows, group, now);
    // Absent, or registered but unresolved: what this group holds is unknown,
    // so no later group can be proven to hold the list's first row either.
    if (!window?.items) return undefined;

    const rows = withoutPendingServerDeletes(window.items, pendingServerDeleteIds);
    if (rows.length === 0) continue;

    // Upcoming rows exist: the target is flag- and layout-dependent.
    if (group === 'upcoming') return undefined;
    const classify = (session: SessionListItem) => getSessionListDateGroup(session, now);
    if (rows.some((session) => classify(session) === 'upcoming')) return undefined;

    // Rows keep their cached (server) order within a group, which is the order
    // the list renders them in, so the first row classified into this group is
    // the list's first row. A row classifying LATER — an unusable date reads as
    // Older — is left to the group it renders in, and when that leaves this
    // group with nothing to offer the walk moves on.
    const first = rows.find((session) => classify(session) === group);
    if (first) return first.session_id;
  }

  return undefined;
}
